import { SearchAPI } from "@ai360/core";
import { ICustomerFieldPageId } from "@ai360/core/dist/4x/es/api/search";
import Immutable from "immutable";
import { select } from "redux-saga/effects";
import { FieldListTabs } from "~/action-panel/components/field-module/actions";
import { IFieldFilterSelections } from "~/action-panel/components/field-module/components/field-list/models";
import { getFilterSelections } from "~/action-panel/components/field-module/components/field-list/selectors";
import { getActiveTab } from "~/action-panel/components/field-module/selectors";
import { AutoExpandedCustomersModifications } from "~/customer-data/actions";
import { getSelectedFieldGuids } from "~/customer-data/selectors";
import { getTheUserGuid } from "~/login/selectors";

export const defaultCustomerFieldPageSize = 50;
export const maxCustomerFieldPageSize = 2147483647;

const searchFilter = (filterSelections: IFieldFilterSelections) =>
    filterSelections.search === "" ? null : filterSelections.search;

const isActive = (tab: FieldListTabs) => tab === FieldListTabs.ACTIVE;

const isInactive = (tab: FieldListTabs) => tab === FieldListTabs.INACTIVE;

const isSelected = (tab: FieldListTabs) => tab === FieldListTabs.SELECTED;

export const activeFilter = (tab: FieldListTabs) =>
    isActive(tab) || isSelected(tab) ? true : isInactive(tab) ? false : null;

export const fieldGuidFilter = (tab: FieldListTabs, selectedFieldGuids: string[]) =>
    isSelected(tab) ? selectedFieldGuids : null;

export const filteredCustomerFieldRequest = function* (
    customerGuids: string[],
    pageSize: number,
    lastPageId: ICustomerFieldPageId
) {
    const selectedFieldGuids = yield select(getSelectedFieldGuids);
    const activeTab: FieldListTabs = yield select(getActiveTab);
    const userGuid: string = yield select(getTheUserGuid);
    const filterSelections: IFieldFilterSelections = yield select(getFilterSelections);

    const request: SearchAPI.ICustomerFieldRequest = {
        userGuid,
        search: searchFilter(filterSelections),
        active: activeFilter(activeTab),
        enrolled: filterSelections.enrolled,
        certifiedOrganic: filterSelections.certifiedOrganic,
        locationOrgLevelGuid: filterSelections.orgLevelGuid,
        fieldGuid: fieldGuidFilter(activeTab, selectedFieldGuids),
        salesperson: filterSelections.salespeople,
        irrigated: filterSelections.irrigated,
        crop: filterSelections.crops,
        classification: filterSelections.classifications,
        customerGuid: customerGuids,
        pageSize,
        lastPageId,
    };
    return request;
};

export const filteredSelectCustomerFieldRequest = (
    active: boolean,
    userGuid: string,
    customerGuid: string,
    filterSelections: IFieldFilterSelections
): SearchAPI.ISelectCustomerFieldRequest => ({
    userGuid,
    search: searchFilter(filterSelections),
    active: active,
    enrolled: filterSelections.enrolled,
    certifiedOrganic: filterSelections.certifiedOrganic,
    locationOrgLevelGuid: filterSelections.orgLevelGuid,
    salesperson: filterSelections.salespeople,
    irrigated: filterSelections.irrigated,
    crop: filterSelections.crops,
    classification: filterSelections.classifications,
    customerGuid,
});

export const filteredFieldRequest = function* (customerIds: string[]) {
    const selectedFieldGuids = yield select(getSelectedFieldGuids);
    const activeTab: FieldListTabs = yield select(getActiveTab);
    const userGuid: string = yield select(getTheUserGuid);
    const filterSelections: IFieldFilterSelections = yield select(getFilterSelections);

    const request: SearchAPI.IFieldRequest = {
        customerGuid: customerIds,
        fieldGuid: fieldGuidFilter(activeTab, selectedFieldGuids),
        userGuid,
        search: filterSelections.search === "" ? null : filterSelections.search,
        active: activeFilter(activeTab),
        certifiedOrganic: filterSelections.certifiedOrganic,
        locationOrgLevelGuid: filterSelections.orgLevelGuid,
        irrigated: filterSelections.irrigated,
        crop: filterSelections.crops,
        classification: filterSelections.classifications,
    };
    return request;
};

export const filteredSummaryRequest = (
    userGuid: string,
    filterSelections: IFieldFilterSelections,
    active: boolean = null
): SearchAPI.ISummaryRequest => ({
    userGuid,
    active,
    search: filterSelections.search === "" ? null : filterSelections.search,
    enrolled: filterSelections.enrolled,
    certifiedOrganic: filterSelections.certifiedOrganic,
    salesperson: filterSelections.salespeople,
    irrigated: filterSelections.irrigated,
    crop: filterSelections.crops,
    classification: filterSelections.classifications,
    locationOrgLevelGuid: filterSelections.orgLevelGuid,
});

export const fieldsSummaryRequest = (
    userGuid: string,
    fields: string[],
    active: boolean = null
): SearchAPI.ISummaryRequest => ({
    userGuid,
    field: fields,
    active: active,
});

export const autoExpandedCustomerIds = (customerFields: SearchAPI.ICustomerFieldResult[]) =>
    customerFields.filter((x) => x.fieldMatchedSearch).map((x) => x.customerId);

export const notAutoExpandedCustomerIds = (customerFields: SearchAPI.ICustomerFieldResult[]) =>
    customerFields.filter((x) => !x.fieldMatchedSearch).map((x) => x.customerId);

export const autoExpandedCustomerModifications = (
    customerFields: SearchAPI.ICustomerFieldResult[]
): AutoExpandedCustomersModifications => ({
    merge: Immutable.Set(autoExpandedCustomerIds(customerFields)),
    remove: notAutoExpandedCustomerIds(customerFields),
});
